import {formatTime, Temporal} from "@/shared/utils/datefunctions";
import * as Button from "@/shared/utils/button";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Input from "@/shared/utils/input";
import {DOMUtil} from "@/shared/utils/dom";
import {CombinedRecordingStatus, WorkingTimeType} from "@/shared/timr";

const DIALOG_TEMPLATE = `
	<div>
		<form class="form-horizontal">
			<fieldset data-working-time-section>
				<div class="form-group">
					<label for="pauseWorkingTimeSwitchWorkingTimeTypeId" class="control-label col-xs-4">${messages["workingTime.workingTimeType"]}</label>
					<div class="col-xs-8">
						<select name="workingTimeTypeId" id="pauseWorkingTimeSwitchWorkingTimeTypeId" class="form-control"></select>
					</div>
				</div>
			</fieldset>
		</form>
	</div>
`;

export interface PauseCombinedRecordingDialogConfig {
	breakTimeTypes: WorkingTimeType[];
	endTime: Temporal.ZonedDateTime | null;
	pauseExecutor: (workingTimeTypeId: number) => JQuery.jqXHR<CombinedRecordingStatus>;
	preselectWorkingTimeType: WorkingTimeType | null;
	onDialogClosed: () => void;
}

/**
 * Web component that renders and controls the pause dialog used for switching to break time (BreakTimeRecordingMode.RECORD_WITH_POSITION)
 */
export class PauseCombinedRecordingDialog extends HTMLElement {

	private config: PauseCombinedRecordingDialogConfig;
	private dialog: BootstrapModal.Modal;
	private workingTimeTypeSelect: JQuery;

	public static show(config: PauseCombinedRecordingDialogConfig): void {
		if (!this.validateDialogPreconditions(config.breakTimeTypes)) {
			return;
		}

		const dialog = document.createElement("pause-combined-recording-dialog") as PauseCombinedRecordingDialog;
		dialog.config = config;
		dialog.show();
	}

	private connectedCallback(): void {
		this.innerHTML = DIALOG_TEMPLATE;
		this.workingTimeTypeSelect = $(this).find("select[name=workingTimeTypeId]");
	}

	private show(): void {
		this.dialog = new BootstrapModal.Modal({
			title: messages.withArguments("switchWorkingTimeTypeDialog.pause.title", formatTime(this.config.endTime)),
			message: $(this),
			size: "modal-lg",
			destroyOnClose: true,
			buttons: [{
				text: "dialog.pause",
				action: () => {
					this.executePauseAction();
				},
				css: "pauseButton btn-primary"
			}, {
				text: "dialog.cancel",
				css: "cancelButton btn-default"
			}],
			closeOnOutsideClick: false,
			onClose: () => {
				this.config.onDialogClosed();
				Input.enable(this.dialog.getButtons("cancelButton"));
				Input.enable(this.dialog.getButtons("pauseButton"));
			},
			onOpen: () => {
				this.updateWorkingTimeTypeSelection();
				this.setLoadingState(false);
			}
		});

		this.dialog.show();
	}

	private updateWorkingTimeTypeSelection(): void {
		DOMUtil.fillWorkingTimeTypeSelect(this.workingTimeTypeSelect, this.config.breakTimeTypes, false);
		if (this.config.preselectWorkingTimeType && DOMUtil.hasOption(this.workingTimeTypeSelect, this.config.preselectWorkingTimeType.id.toString())) {
			this.workingTimeTypeSelect.val(this.config.preselectWorkingTimeType.id);
		}
	}

	private executePauseAction(): void {
		const workingTimeTypeId = this.dialog.find("#pauseWorkingTimeSwitchWorkingTimeTypeId").val() as number;
		this.setLoadingState(true);
		if (!workingTimeTypeId) {
			this.setLoadingState(false);
			BootstrapModal.error({message: "combinedRecording.workingTimeTypeId.required"});
			return;
		}

		// execute pause request; reset state on error (success closes the dialog immediately)
		this.config.pauseExecutor(workingTimeTypeId)
			.done(() => this.dialog.close())
			.fail(() => this.setLoadingState(false));
	}

	private setLoadingState(setLoading: boolean): void {
		const inputFields = this.dialog.find("input:not([type='checkbox']), select");
		inputFields.prop("disabled", setLoading);

		const cancelButton = this.dialog.getButtons("cancelButton");
		const pauseButton = this.dialog.getButtons("pauseButton");
		if (setLoading) {
			Button.setLoading(pauseButton);
		} else {
			Button.resetLoading(pauseButton);
		}
		Input.toggle(cancelButton, !setLoading);
	}

	private static validateDialogPreconditions(breakTimeTypes: WorkingTimeType[]): boolean {
		if (breakTimeTypes.length === 0) {
			BootstrapModal.error({message: "recording.switchToBreakTime.noBreakTime"});
			return false;
		}

		return true;
	}
}
